import $ from "jquery";
import AddToWishlistButton from "./product-add-to-wishlist.js";

class WishlistManager {
  constructor() {
    this.wishlistData = new Map(); // product_id => wishlist_item
    this.buttons = new Map(); // product_id => Array of AddToWishlistButton instances
    this.isInitialized = false;
  }

  async init() {
    if (this.isInitialized) {
      return;
    }

    // First, load wishlist count for badge
    await this.loadWishlistCount();

    // Collect all product IDs from wishlist buttons
    const productIds = [];
    $(".add-to-wishlist-btn").each(function () {
      const rawProductId = $(this).data("product-id");
      const productId = String(rawProductId);
      if (productId && !productIds.includes(productId)) {
        productIds.push(productId);
      }
    });

    if (productIds.length === 0) {
      return;
    }

    // Note: Wishlist data is already loaded in loadWishlistCount()
    // We only need to update buttons with existing data

    // Set initialized flag BEFORE creating buttons to prevent individual API calls
    this.isInitialized = true;

    // Initialize all buttons with pre-loaded data
    $(".add-to-wishlist-btn").each((index, element) => {
      const rawProductId = $(element).data("product-id");
      const productId = String(rawProductId);
      const wishlistItem = this.wishlistData.get(productId);

      // Create button instance with pre-loaded data
      const buttonInstance = new AddToWishlistButton(element, {
        preloadedWishlistItem: wishlistItem,
      });

      // Store all buttons for the same product ID
      if (!this.buttons.has(productId)) {
        this.buttons.set(productId, []);
      }
      this.buttons.get(productId).push(buttonInstance);
    });

    // Initialize remove buttons in wishlist page
    this.initRemoveButtons();
  }

  async fetchBatchWishlistStatus(productIds) {
    // Try batch API first
    const batchUrl = `/wishlist/mywishlistajax?product_id_list=${productIds.join(
      ","
    )}`;

    try {
      const response = await fetch(batchUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        return await this.fetchIndividualWishlistStatus(productIds);
      }
    } catch (error) {
      return await this.fetchIndividualWishlistStatus(productIds);
    }
  }

  async fetchIndividualWishlistStatus(productIds) {
    const allItems = [];

    for (const productId of productIds) {
      try {
        const url = `/wishlist/mywishlistajax?product_id=${productId}`;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.items) {
            allItems.push(...data.items);
          }
        }
      } catch (error) {
        // Error fetching product
      }
    }

    return {
      success: true,
      items: allItems,
      total: allItems.length,
    };
  }

  // Method to update wishlist data when items are added/removed
  updateWishlistData(productId, wishlistItem) {
    if (wishlistItem) {
      this.wishlistData.set(productId, wishlistItem);
    } else {
      this.wishlistData.delete(productId);
    }

    // Update all buttons for this product ID
    const buttons = this.buttons.get(productId);
    if (buttons) {
      buttons.forEach((buttonInstance) => {
        if (wishlistItem) {
          buttonInstance.setWishlistId(wishlistItem.id);
          buttonInstance.setActive(true);
        } else {
          buttonInstance.setWishlistId(0);
          buttonInstance.setActive(false);
        }
      });
    }

    // Update wishlist badge
    this.updateWishlistBadge();

    // Update cookie for guest users
    this.updateWishlistCookie();
  }

  // Method to update wishlist cookie (for guest users)
  updateWishlistCookie() {
    try {
      const cookieName = "OVWISHLIST";
      const maxItems = 50;
      const expireTime = new Date();
      expireTime.setTime(expireTime.getTime() + 30 * 24 * 60 * 60 * 1000); // 30 days

      // Convert wishlist data to simple product ID list
      const productIds = Array.from(this.wishlistData.keys()).map((id) =>
        parseInt(id)
      );

      // Keep only maximum number of items
      if (productIds.length > maxItems) {
        productIds.splice(maxItems);
      }

      // Set the cookie with comma-separated product IDs
      const cookieValue = productIds.join(",");
      document.cookie = `${cookieName}=${cookieValue}; expires=${expireTime.toUTCString()}; path=/; SameSite=Lax`;
    } catch (error) {
      // Error updating wishlist cookie
    }
  }

  // Method to get wishlist data for a specific product
  getWishlistData(productId) {
    return this.wishlistData.get(productId);
  }

  // Method to get total wishlist count
  getWishlistCount() {
    return this.wishlistData.size;
  }

  // Method to update wishlist badge
  updateWishlistBadge() {
    const count = this.getWishlistCount();

    // Update desktop badge
    const desktopBadge = document.getElementById("wishlist-quantity-badge");
    if (desktopBadge) {
      desktopBadge.textContent = count;
      desktopBadge.style.display = count > 0 ? "inline-block" : "none";
    }

    // Update mobile badge
    const mobileBadge = document.getElementById(
      "wishlist-quantity-badge-mobile"
    );
    if (mobileBadge) {
      mobileBadge.textContent = count;
      mobileBadge.style.display = count > 0 ? "inline-block" : "none";
    }
  }

  // Method to load wishlist count from server
  async loadWishlistCount() {
    try {
      const response = await fetch("/wishlist/mywishlistajax", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (response.ok) {
        const responseData = await response.json();

        if (responseData.success && responseData.data && responseData.data.items) {
          // Update wishlist data with all items
          this.wishlistData.clear();
          responseData.data.items.forEach((item) => {
            // Now both logged-in and guest users use the same format: product_id
            const productId = String(item.product_id);
            if (productId) {
              this.wishlistData.set(productId, item);
            }
          });

          // Update badge
          this.updateWishlistBadge();
        }
      }
    } catch (error) {
      // Fallback to cookie for guest users
      this.loadWishlistCountFromCookie();
    }
  }

  // Method to load wishlist count from cookie (for guest users)
  loadWishlistCountFromCookie() {
    try {
      const cookieName = "OVWISHLIST";
      const productIds = [];

      // Get existing product IDs from cookie
      if (document.cookie.includes(cookieName + "=")) {
        const cookieValue = document.cookie
          .split("; ")
          .find((row) => row.startsWith(cookieName + "="))
          ?.split("=")[1];

        if (cookieValue) {
          productIds.push(
            ...cookieValue
              .split(",")
              .map((id) => parseInt(id.trim()))
              .filter((id) => !isNaN(id))
          );
        }
      }

      // Update wishlist data with cookie items (convert to unified format)
      this.wishlistData.clear();
      productIds.forEach((productId) => {
        const wishlistItem = {
          id: productId, // For guest, id = product_id
          product_id: productId,
          from: 1,
          date_created: Date.now(),
          date_modified: Date.now(),
        };
        this.wishlistData.set(String(productId), wishlistItem);
      });

      // Update badge
      this.updateWishlistBadge();
    } catch (error) {
      // Error loading wishlist count from cookie
    }
  }

  // Method to reinitialize buttons after AJAX content load
  async reinitializeButtons() {
    // Clear existing button instances and wishlist data
    this.buttons.clear();
    this.wishlistData.clear();

    // Find all new wishlist buttons
    const newButtons = document.querySelectorAll(".add-to-wishlist-btn");

    if (newButtons.length === 0) {
      return;
    }

    // Collect product IDs from new buttons
    const productIds = [];
    newButtons.forEach((button) => {
      const productId = button.dataset.productId;
      if (productId && !productIds.includes(productId)) {
        productIds.push(productId);
      }
    });

    // If we have new product IDs, fetch their wishlist status
    if (productIds.length > 0) {
      try {
        const response = await this.fetchBatchWishlistStatus(productIds);

        if (response.success && response.data && response.data.items) {
          // Update wishlist data with new items
          response.data.items.forEach((item) => {
            // Now both logged-in and guest users use the same format: product_id
            const productId = String(item.product_id);
            if (productId) {
              this.wishlistData.set(productId, item);
            }
          });
        }
      } catch (error) {
        // Error fetching new wishlist status
      }
    }

    // Initialize all new buttons
    newButtons.forEach((element) => {
      const productId = element.dataset.productId;
      const wishlistItem = this.wishlistData.get(productId);

      // Create button instance with pre-loaded data
      const buttonInstance = new AddToWishlistButton(element, {
        preloadedWishlistItem: wishlistItem,
      });

      // Store all buttons for the same product ID
      if (!this.buttons.has(productId)) {
        this.buttons.set(productId, []);
      }
      this.buttons.get(productId).push(buttonInstance);
    });
  }

  // Method to refresh all buttons after a change
  refreshAllButtons() {
    this.buttons.forEach((buttonInstances, productId) => {
      const wishlistItem = this.wishlistData.get(productId);
      buttonInstances.forEach((buttonInstance) => {
        if (wishlistItem) {
          buttonInstance.setWishlistId(wishlistItem.id);
          buttonInstance.setActive(true);
        } else {
          buttonInstance.setWishlistId(0);
          buttonInstance.setActive(false);
        }
      });
    });
  }

  // Method to initialize remove buttons in wishlist page
  initRemoveButtons() {
    $(".remove-from-wishlist")
      .off("click")
      .on("click", async (e) => {
        e.preventDefault();
        const button = $(e.currentTarget);
        const productId = button.data("product-id");
        const wishlistId = button.data("wishlist-id");

        if (!productId || !wishlistId) {
          return;
        }

        try {
          // Show loading state
          button.prop("disabled", true);
          button.html(
            '<svg class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path></svg>'
          );

          // Call remove API
          const response = await fetch(
            `/wishlist/removeajax?id=${encodeURIComponent(wishlistId)}`,
            {
              method: "GET",
              headers: {
                "X-Requested-With": "XMLHttpRequest",
              },
            }
          );

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();

          if (data.success) {
            // Update wishlist data
            this.updateWishlistData(productId, null);

            // Remove the entire product card from UI
            const productCard = button.closest(".relative.group");

            if (productCard.length > 0) {
              productCard.fadeOut(300, function () {
                $(this).remove();
              });
            } else {
              // Fallback: remove the button's parent container
              button.parent().fadeOut(300, function () {
                $(this).remove();
              });
            }

            // Check if wishlist is empty
            setTimeout(() => {
              if ($(".remove-from-wishlist").length === 0) {
                // Reload page to show empty state
                window.location.reload();
              }
            }, 500);
          } else {
            throw new Error(
              data.errors?.join(". ") || "Failed to remove from wishlist"
            );
          }
        } catch (error) {
          // Restore button
          button.prop("disabled", false);
          button.html(
            '<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>'
          );

          // Show error message
          alert(
            "Có lỗi xảy ra khi xóa sản phẩm khỏi danh sách yêu thích. Vui lòng thử lại."
          );
        }
      });
  }
}

// Create global instance
const wishlistManagerInstance = new WishlistManager();
window.WishlistManager = wishlistManagerInstance;

export default WishlistManager;
